import { useCallback, useEffect, useState } from "react";
import DataTable, { Column, FilterOption } from "../../components/commons/DataTable";
import PostAction from "../../components/partials/post/PostAction";
import type { Post, SearchPostParams } from "../../services/webServices/post/types";
import { getPosts } from "../../services/webServices/post/get";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";

const PAGE_SIZE = 12;

const filterOptions: FilterOption[] = [
  { value: "published", label: "منتشر شده" },
  { value: "unpublished", label: "در انتظار تایید" }
];

export default function Post() {

  const [data, setData] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filterValue, setFilterValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  const fetchPost = useCallback(async () => {

    if (isFirstLoad) showLoading();
    setIsLoading(true);

    const params: SearchPostParams = {
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
    };

    if (filterValue === "published") {
      params.is_published = true;
    }

    if (filterValue === "unpublished") {
      params.is_published = false;
    }

    const response = await getPosts({ params });

    if (Array.isArray(response)) {
      setData(response);

      if (response.length < PAGE_SIZE) {
        setTotalItems((currentPage - 1) * PAGE_SIZE + response.length);
      } else {
        setTotalItems(currentPage * PAGE_SIZE + 1);
      }
    } else {
      setData([]);
      setTotalItems(0);
    }

    setIsLoading(false);
    if (isFirstLoad) hideLoading();
  }, [currentPage, filterValue]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (value: string) => {
    setFilterValue(value);
    setCurrentPage(1);
  };

  const handleOnRowClick = (row: Post) => {
    window.open(`https://danajou.com/post/djp-${row.id}`, "_blank");
  };

  const handleRenderAction = (row:  Post) => {
    return (
      <PostAction
        post={row}
        onActionSuccessed={() => fetchPost()}
      />
    );
  };

  const columns: Column<Post>[] = [
    {
      key: "description",
      header: "متن پست",
      align: "right",
      sortable: false,
      width: "45%",
      accessor: (row) => row.description
    },
    {
      key: "author",
      header: "نویسنده",
      align: "right",
      sortable: true,
      width: "25%",
      accessor: (row) => (
        <div className="flex items-center gap-2">
          <img
            className="h-[20px] w-[20px] rounded-full object-cover"
            src={process.env.REACT_APP_File_URL + row.user.avatar || "/images/avatar-alt.jpg"}
            alt=""
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/images/avatar-alt.jpg";
            }}
          />
          <span>{row.user.full_name || "بدون نام"}</span>
        </div>
      )
    },
    {
      key: "created_at",
      header: "زمان ایجاد",
      align: "right",
      sortable: true,
      width: "15%",
      accessor: (row) => (
        <span>
          {row.created_at
            ? new Date(row.created_at).toLocaleDateString("fa-IR")
            : "بدون تاریخ"}
        </span>
      )
    }
  ];

  return (
    <div>
      <DataTable
        title="لیست پست‌ها"
        data={data}
        columns={columns}
        loading={isLoading}
        loadingText="در حال بارگذاری پست‌ها..."
        emptyText="پستی یافت نشد"

        filterable={true}
        filterPlaceholder="همه پست‌ها"
        filterOptions={filterOptions}
        filterValue={filterValue}
        onFilterChange={handleFilterChange}

        pagination={true}
        serverSidePagination={true}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        totalItems={totalItems}
        pageSize={PAGE_SIZE}

        onRowClick={handleOnRowClick}
        renderActions={handleRenderAction}
      />
    </div>
  );
};